import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { updateTenant, fetchAllowedUsers, refreshTargets, listLocationsForConnectedAccount, createLocationForConnectedAccount, calculateFees } from '../redux/actions';
import LogoSection from '../components/LogoSection';
import { collection, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase.utils';

const TenantDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const tenants = useSelector((state) => state.tenants);
    const userRole = useSelector((state) => state.userRole);
    const [isEditing, setIsEditing] = useState(false);
    const [targets, setTargets] = useState([]);
    const [allowedUsers, setAllowedUsers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);
    const [locationsError, setLocationsError] = useState(null);
    const [showCreateLocationForm, setShowCreateLocationForm] = useState(false);
    const [creatingLocation, setCreatingLocation] = useState(false);
    const [createLocationMessage, setCreateLocationMessage] = useState(null);
    const [newLocation, setNewLocation] = useState({
        display_name: '',
        address: {
            line1: '700-305 Milner Ave',
            city: 'Toronto',
            state: 'ON',
            country: 'CA',
            postal_code: 'M1B 3V4'
        }
    });
    const [feeAmount, setFeeAmount] = useState('');
    const [feeResult, setFeeResult] = useState(null);
    const [calculatingFees, setCalculatingFees] = useState(false);

    // Default values
    const defaultValues = {
        tenantName: '',
        id: '',
        logo: '',
        org_id: '',
        clientAccountId: '',
        cash_url: 'https://paymentfunctions-uploadcampaigncauseprojecttransa-lsxnwb2pga-uc.a.run.app/',
        defaultTarget: 'lKWHLGcx1j4LoGVjqRJR',
        defaultTargetId: 'lKWHLGcx1j4LoGVjqRJR',
        fixedRate: 1.3,
        variablePercentage: 2.3,
        terminalId: '123',
        amt1: 10,
        amt2: 20,
        amt3: 50,
        amt4: 100,
        CCY: 'CAD',
        Band1MinAmt: 5,
        Band1MaxAmt: 9.99,
        Band1Fixed: 0,
        Band1Variable: 1,
        Band2MinAmt: 10,
        Band2MaxAmt: 99.99,
        Band2Fixed: 1,
        Band2Variable: 0,
        Band3MinAmt: 100,
        Band3MaxAmt: 100000,
        Band3Fixed: 0,
        Band3Variable: 1
    };

    const [formData, setFormData] = useState(defaultValues);

    useEffect(() => {
        const loadTenantData = async () => {
            if (!tenantId) return;
            
            try {
                const tenantRef = doc(db, "tenants", tenantId);
                const tenantSnap = await getDoc(tenantRef);
                
                if (tenantSnap.exists()) {
                    const tenant = tenantSnap.data();
                    
                    // Check if band fields exist, if not, save defaults
                    const hasBandFields = tenant.CCY !== undefined;
                    
                    if (!hasBandFields) {
                        // Save default band values
                        await setDoc(tenantRef, {
                            CCY: defaultValues.CCY,
                            Band1MinAmt: defaultValues.Band1MinAmt,
                            Band1MaxAmt: defaultValues.Band1MaxAmt,
                            Band1Fixed: defaultValues.Band1Fixed,
                            Band1Variable: defaultValues.Band1Variable,
                            Band2MinAmt: defaultValues.Band2MinAmt,
                            Band2MaxAmt: defaultValues.Band2MaxAmt,
                            Band2Fixed: defaultValues.Band2Fixed,
                            Band2Variable: defaultValues.Band2Variable,
                            Band3MinAmt: defaultValues.Band3MinAmt,
                            Band3MaxAmt: defaultValues.Band3MaxAmt,
                            Band3Fixed: defaultValues.Band3Fixed,
                            Band3Variable: defaultValues.Band3Variable
                        }, { merge: true });
                        
                        // Re-fetch to get updated data
                        const updatedSnap = await getDoc(tenantRef);
                        const updatedTenant = updatedSnap.data();
                        setFormData({
                            tenantName: updatedTenant.tenantName || defaultValues.tenantName,
                            id: updatedTenant.id || defaultValues.id,
                            logo: updatedTenant.logo || defaultValues.logo,
                            org_id: updatedTenant.org_id || defaultValues.org_id,
                            clientAccountId: updatedTenant.clientAccountId || defaultValues.clientAccountId,
                            cash_url: updatedTenant.cash_url || defaultValues.cash_url,
                            defaultTarget: updatedTenant.defaultTarget || defaultValues.defaultTarget,
                            defaultTargetId: updatedTenant.defaultTargetId || defaultValues.defaultTargetId,
                            fixedRate: updatedTenant.fixedRate !== undefined ? updatedTenant.fixedRate : defaultValues.fixedRate,
                            variablePercentage: updatedTenant.variablePercentage !== undefined ? updatedTenant.variablePercentage : defaultValues.variablePercentage,
                            terminalId: updatedTenant.terminalId || defaultValues.terminalId,
                            amt1: updatedTenant.amt1 !== undefined ? updatedTenant.amt1 : defaultValues.amt1,
                            amt2: updatedTenant.amt2 !== undefined ? updatedTenant.amt2 : defaultValues.amt2,
                            amt3: updatedTenant.amt3 !== undefined ? updatedTenant.amt3 : defaultValues.amt3,
                            amt4: updatedTenant.amt4 !== undefined ? updatedTenant.amt4 : defaultValues.amt4,
                            CCY: updatedTenant.CCY || defaultValues.CCY,
                            Band1MinAmt: updatedTenant.Band1MinAmt !== undefined ? updatedTenant.Band1MinAmt : defaultValues.Band1MinAmt,
                            Band1MaxAmt: updatedTenant.Band1MaxAmt !== undefined ? updatedTenant.Band1MaxAmt : defaultValues.Band1MaxAmt,
                            Band1Fixed: updatedTenant.Band1Fixed !== undefined ? updatedTenant.Band1Fixed : defaultValues.Band1Fixed,
                            Band1Variable: updatedTenant.Band1Variable !== undefined ? updatedTenant.Band1Variable : defaultValues.Band1Variable,
                            Band2MinAmt: updatedTenant.Band2MinAmt !== undefined ? updatedTenant.Band2MinAmt : defaultValues.Band2MinAmt,
                            Band2MaxAmt: updatedTenant.Band2MaxAmt !== undefined ? updatedTenant.Band2MaxAmt : defaultValues.Band2MaxAmt,
                            Band2Fixed: updatedTenant.Band2Fixed !== undefined ? updatedTenant.Band2Fixed : defaultValues.Band2Fixed,
                            Band2Variable: updatedTenant.Band2Variable !== undefined ? updatedTenant.Band2Variable : defaultValues.Band2Variable,
                            Band3MinAmt: updatedTenant.Band3MinAmt !== undefined ? updatedTenant.Band3MinAmt : defaultValues.Band3MinAmt,
                            Band3MaxAmt: updatedTenant.Band3MaxAmt !== undefined ? updatedTenant.Band3MaxAmt : defaultValues.Band3MaxAmt,
                            Band3Fixed: updatedTenant.Band3Fixed !== undefined ? updatedTenant.Band3Fixed : defaultValues.Band3Fixed,
                            Band3Variable: updatedTenant.Band3Variable !== undefined ? updatedTenant.Band3Variable : defaultValues.Band3Variable
                        });
                    } else {
                        // Band fields exist, load normally
                        setFormData({
                            tenantName: tenant.tenantName || defaultValues.tenantName,
                            id: tenant.id || defaultValues.id,
                            logo: tenant.logo || defaultValues.logo,
                            org_id: tenant.org_id || defaultValues.org_id,
                            clientAccountId: tenant.clientAccountId || defaultValues.clientAccountId,
                            cash_url: tenant.cash_url || defaultValues.cash_url,
                            defaultTarget: tenant.defaultTarget || defaultValues.defaultTarget,
                            defaultTargetId: tenant.defaultTargetId || defaultValues.defaultTargetId,
                            fixedRate: tenant.fixedRate !== undefined ? tenant.fixedRate : defaultValues.fixedRate,
                            variablePercentage: tenant.variablePercentage !== undefined ? tenant.variablePercentage : defaultValues.variablePercentage,
                            terminalId: tenant.terminalId || defaultValues.terminalId,
                            amt1: tenant.amt1 !== undefined ? tenant.amt1 : defaultValues.amt1,
                            amt2: tenant.amt2 !== undefined ? tenant.amt2 : defaultValues.amt2,
                            amt3: tenant.amt3 !== undefined ? tenant.amt3 : defaultValues.amt3,
                            amt4: tenant.amt4 !== undefined ? tenant.amt4 : defaultValues.amt4,
                            CCY: tenant.CCY || defaultValues.CCY,
                            Band1MinAmt: tenant.Band1MinAmt !== undefined ? tenant.Band1MinAmt : defaultValues.Band1MinAmt,
                            Band1MaxAmt: tenant.Band1MaxAmt !== undefined ? tenant.Band1MaxAmt : defaultValues.Band1MaxAmt,
                            Band1Fixed: tenant.Band1Fixed !== undefined ? tenant.Band1Fixed : defaultValues.Band1Fixed,
                            Band1Variable: tenant.Band1Variable !== undefined ? tenant.Band1Variable : defaultValues.Band1Variable,
                            Band2MinAmt: tenant.Band2MinAmt !== undefined ? tenant.Band2MinAmt : defaultValues.Band2MinAmt,
                            Band2MaxAmt: tenant.Band2MaxAmt !== undefined ? tenant.Band2MaxAmt : defaultValues.Band2MaxAmt,
                            Band2Fixed: tenant.Band2Fixed !== undefined ? tenant.Band2Fixed : defaultValues.Band2Fixed,
                            Band2Variable: tenant.Band2Variable !== undefined ? tenant.Band2Variable : defaultValues.Band2Variable,
                            Band3MinAmt: tenant.Band3MinAmt !== undefined ? tenant.Band3MinAmt : defaultValues.Band3MinAmt,
                            Band3MaxAmt: tenant.Band3MaxAmt !== undefined ? tenant.Band3MaxAmt : defaultValues.Band3MaxAmt,
                            Band3Fixed: tenant.Band3Fixed !== undefined ? tenant.Band3Fixed : defaultValues.Band3Fixed,
                            Band3Variable: tenant.Band3Variable !== undefined ? tenant.Band3Variable : defaultValues.Band3Variable
                        });
                    }
                } else {
                    // If tenant not found, show defaults
                    setFormData(defaultValues);
                }
            } catch (error) {
                console.error("Error loading tenant data:", error);
                // Fallback to Redux state if available
        const tenant = tenants.find(t => t.id === tenantId);
        if (tenant) {
            setFormData({
                        tenantName: tenant.tenantName || defaultValues.tenantName,
                        id: tenant.id || defaultValues.id,
                        logo: tenant.logo || defaultValues.logo,
                        org_id: tenant.org_id || defaultValues.org_id,
                        clientAccountId: tenant.clientAccountId || defaultValues.clientAccountId,
                        cash_url: tenant.cash_url || defaultValues.cash_url,
                        defaultTarget: tenant.defaultTarget || defaultValues.defaultTarget,
                        defaultTargetId: tenant.defaultTargetId || defaultValues.defaultTargetId,
                        fixedRate: tenant.fixedRate !== undefined ? tenant.fixedRate : defaultValues.fixedRate,
                        variablePercentage: tenant.variablePercentage !== undefined ? tenant.variablePercentage : defaultValues.variablePercentage,
                        terminalId: tenant.terminalId || defaultValues.terminalId,
                        amt1: tenant.amt1 !== undefined ? tenant.amt1 : defaultValues.amt1,
                        amt2: tenant.amt2 !== undefined ? tenant.amt2 : defaultValues.amt2,
                        amt3: tenant.amt3 !== undefined ? tenant.amt3 : defaultValues.amt3,
                        amt4: tenant.amt4 !== undefined ? tenant.amt4 : defaultValues.amt4,
                        CCY: tenant.CCY || defaultValues.CCY,
                        Band1MinAmt: tenant.Band1MinAmt !== undefined ? tenant.Band1MinAmt : defaultValues.Band1MinAmt,
                        Band1MaxAmt: tenant.Band1MaxAmt !== undefined ? tenant.Band1MaxAmt : defaultValues.Band1MaxAmt,
                        Band1Fixed: tenant.Band1Fixed !== undefined ? tenant.Band1Fixed : defaultValues.Band1Fixed,
                        Band1Variable: tenant.Band1Variable !== undefined ? tenant.Band1Variable : defaultValues.Band1Variable,
                        Band2MinAmt: tenant.Band2MinAmt !== undefined ? tenant.Band2MinAmt : defaultValues.Band2MinAmt,
                        Band2MaxAmt: tenant.Band2MaxAmt !== undefined ? tenant.Band2MaxAmt : defaultValues.Band2MaxAmt,
                        Band2Fixed: tenant.Band2Fixed !== undefined ? tenant.Band2Fixed : defaultValues.Band2Fixed,
                        Band2Variable: tenant.Band2Variable !== undefined ? tenant.Band2Variable : defaultValues.Band2Variable,
                        Band3MinAmt: tenant.Band3MinAmt !== undefined ? tenant.Band3MinAmt : defaultValues.Band3MinAmt,
                        Band3MaxAmt: tenant.Band3MaxAmt !== undefined ? tenant.Band3MaxAmt : defaultValues.Band3MaxAmt,
                        Band3Fixed: tenant.Band3Fixed !== undefined ? tenant.Band3Fixed : defaultValues.Band3Fixed,
                        Band3Variable: tenant.Band3Variable !== undefined ? tenant.Band3Variable : defaultValues.Band3Variable
            });
                } else {
                    setFormData(defaultValues);
                }
            }
        };
        
        loadTenantData();
    }, [tenantId, tenants]);

    useEffect(() => {
        const fetchTargets = async () => {
            try {
                const targetsRef = collection(db, "tenants", tenantId, "fundraisingTargets");
                const querySnapshot = await getDocs(targetsRef);
                const targetsData = [];
                querySnapshot.forEach((doc) => {
                    targetsData.push({
                        id: doc.id,
                        ...doc.data()
                    });
                });
                setTargets(targetsData);
            } catch (error) {
                console.error("Error fetching targets:", error);
            }
        };

        if (tenantId) {
            fetchTargets();
        }
    }, [tenantId]);

    useEffect(() => {
        if (tenantId) {
            dispatch(fetchAllowedUsers(tenantId)).then(users => {
                setAllowedUsers(users);
            });
        }
    }, [tenantId, dispatch]);

    // Auto-fetch locations when page opens and clientAccountId is available
    useEffect(() => {
        if (formData.clientAccountId && tenantId && !loadingLocations && locations.length === 0) {
            handleFetchLocations();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.clientAccountId, tenantId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const numericFields = ['fixedRate', 'variablePercentage', 'amt1', 'amt2', 'amt3', 'amt4',
            'Band1MinAmt', 'Band1MaxAmt', 'Band1Fixed', 'Band1Variable',
            'Band2MinAmt', 'Band2MaxAmt', 'Band2Fixed', 'Band2Variable',
            'Band3MinAmt', 'Band3MaxAmt', 'Band3Fixed', 'Band3Variable'];
        
        setFormData(prev => ({
            ...prev,
            [name]: numericFields.includes(name)
                ? (value === '' ? '' : parseFloat(value) || 0)
                : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await dispatch(updateTenant(tenantId, formData));
        setIsEditing(false);
        
        // Re-fetch from DB to validate save
        try {
            const tenantRef = doc(db, "tenants", tenantId);
            const tenantSnap = await getDoc(tenantRef);
            if (tenantSnap.exists()) {
                const tenant = tenantSnap.data();
                setFormData(prev => ({
                    ...prev,
                    CCY: tenant.CCY || defaultValues.CCY,
                    Band1MinAmt: tenant.Band1MinAmt !== undefined ? tenant.Band1MinAmt : defaultValues.Band1MinAmt,
                    Band1MaxAmt: tenant.Band1MaxAmt !== undefined ? tenant.Band1MaxAmt : defaultValues.Band1MaxAmt,
                    Band1Fixed: tenant.Band1Fixed !== undefined ? tenant.Band1Fixed : defaultValues.Band1Fixed,
                    Band1Variable: tenant.Band1Variable !== undefined ? tenant.Band1Variable : defaultValues.Band1Variable,
                    Band2MinAmt: tenant.Band2MinAmt !== undefined ? tenant.Band2MinAmt : defaultValues.Band2MinAmt,
                    Band2MaxAmt: tenant.Band2MaxAmt !== undefined ? tenant.Band2MaxAmt : defaultValues.Band2MaxAmt,
                    Band2Fixed: tenant.Band2Fixed !== undefined ? tenant.Band2Fixed : defaultValues.Band2Fixed,
                    Band2Variable: tenant.Band2Variable !== undefined ? tenant.Band2Variable : defaultValues.Band2Variable,
                    Band3MinAmt: tenant.Band3MinAmt !== undefined ? tenant.Band3MinAmt : defaultValues.Band3MinAmt,
                    Band3MaxAmt: tenant.Band3MaxAmt !== undefined ? tenant.Band3MaxAmt : defaultValues.Band3MaxAmt,
                    Band3Fixed: tenant.Band3Fixed !== undefined ? tenant.Band3Fixed : defaultValues.Band3Fixed,
                    Band3Variable: tenant.Band3Variable !== undefined ? tenant.Band3Variable : defaultValues.Band3Variable
                }));
            }
        } catch (error) {
            console.error("Error validating save:", error);
        }
    };

    const handleCancel = async () => {
        // Reset to original values from DB
        try {
            const tenantRef = doc(db, "tenants", tenantId);
            const tenantSnap = await getDoc(tenantRef);
            if (tenantSnap.exists()) {
                const tenant = tenantSnap.data();
                setFormData({
                    tenantName: tenant.tenantName || defaultValues.tenantName,
                    id: tenant.id || defaultValues.id,
                    logo: tenant.logo || defaultValues.logo,
                    org_id: tenant.org_id || defaultValues.org_id,
                    clientAccountId: tenant.clientAccountId || defaultValues.clientAccountId,
                    cash_url: tenant.cash_url || defaultValues.cash_url,
                    defaultTarget: tenant.defaultTarget || defaultValues.defaultTarget,
                    defaultTargetId: tenant.defaultTargetId || defaultValues.defaultTargetId,
                    fixedRate: tenant.fixedRate !== undefined ? tenant.fixedRate : defaultValues.fixedRate,
                    variablePercentage: tenant.variablePercentage !== undefined ? tenant.variablePercentage : defaultValues.variablePercentage,
                    terminalId: tenant.terminalId || defaultValues.terminalId,
                    amt1: tenant.amt1 !== undefined ? tenant.amt1 : defaultValues.amt1,
                    amt2: tenant.amt2 !== undefined ? tenant.amt2 : defaultValues.amt2,
                    amt3: tenant.amt3 !== undefined ? tenant.amt3 : defaultValues.amt3,
                    amt4: tenant.amt4 !== undefined ? tenant.amt4 : defaultValues.amt4,
                    CCY: tenant.CCY || defaultValues.CCY,
                    Band1MinAmt: tenant.Band1MinAmt !== undefined ? tenant.Band1MinAmt : defaultValues.Band1MinAmt,
                    Band1MaxAmt: tenant.Band1MaxAmt !== undefined ? tenant.Band1MaxAmt : defaultValues.Band1MaxAmt,
                    Band1Fixed: tenant.Band1Fixed !== undefined ? tenant.Band1Fixed : defaultValues.Band1Fixed,
                    Band1Variable: tenant.Band1Variable !== undefined ? tenant.Band1Variable : defaultValues.Band1Variable,
                    Band2MinAmt: tenant.Band2MinAmt !== undefined ? tenant.Band2MinAmt : defaultValues.Band2MinAmt,
                    Band2MaxAmt: tenant.Band2MaxAmt !== undefined ? tenant.Band2MaxAmt : defaultValues.Band2MaxAmt,
                    Band2Fixed: tenant.Band2Fixed !== undefined ? tenant.Band2Fixed : defaultValues.Band2Fixed,
                    Band2Variable: tenant.Band2Variable !== undefined ? tenant.Band2Variable : defaultValues.Band2Variable,
                    Band3MinAmt: tenant.Band3MinAmt !== undefined ? tenant.Band3MinAmt : defaultValues.Band3MinAmt,
                    Band3MaxAmt: tenant.Band3MaxAmt !== undefined ? tenant.Band3MaxAmt : defaultValues.Band3MaxAmt,
                    Band3Fixed: tenant.Band3Fixed !== undefined ? tenant.Band3Fixed : defaultValues.Band3Fixed,
                    Band3Variable: tenant.Band3Variable !== undefined ? tenant.Band3Variable : defaultValues.Band3Variable
                });
            }
        } catch (error) {
            console.error("Error loading tenant data for cancel:", error);
        }
        setIsEditing(false);
    };

    const handleFetchLocations = async () => {
        if (!formData.clientAccountId) {
            setLocationsError('Client Account ID is required');
            return;
        }

        setLoadingLocations(true);
        setLocationsError(null);
        
        const result = await dispatch(listLocationsForConnectedAccount(formData.clientAccountId, tenantId));
        
        if (result.success) {
            setLocations(result.locations);
        } else {
            setLocationsError(result.error || 'Failed to fetch locations');
            setLocations([]);
        }
        
        setLoadingLocations(false);
    };

    const handleCreateLocation = async () => {
        if (!formData.clientAccountId) {
            setCreateLocationMessage({ type: 'error', text: 'Client Account ID is required' });
            return;
        }

        setCreatingLocation(true);
        setCreateLocationMessage(null);

        const addressData = {
            line1: newLocation.address.line1 || '700-305 Milner Ave',
            city: newLocation.address.city || 'Toronto',
            state: newLocation.address.state || 'ON',
            country: newLocation.address.country || 'CA',
            postal_code: newLocation.address.postal_code || 'M1B 3V4'
        };

        const result = await dispatch(createLocationForConnectedAccount(
            formData.clientAccountId,
            newLocation.display_name || undefined,
            addressData
        ));

        if (result.success) {
            setCreateLocationMessage({ 
                type: 'success', 
                text: result.message || 'Location created successfully!' 
            });
            setNewLocation({
                display_name: '',
                address: {
                    line1: '700-305 Milner Ave',
                    city: 'Toronto',
                    state: 'ON',
                    country: 'CA',
                    postal_code: 'M1B 3V4'
                }
            });
            setShowCreateLocationForm(false);
            // Re-fetch locations after successful creation
            setTimeout(() => {
                handleFetchLocations();
            }, 500);
        } else {
            setCreateLocationMessage({ 
                type: 'error', 
                text: result.error || 'Failed to create location' 
            });
        }

        setCreatingLocation(false);
    };

    const handleAddressChange = (field, value) => {
        setNewLocation(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [field]: value
            }
        }));
    };

    const handleCalculateFees = async () => {
        if (!feeAmount || isNaN(feeAmount) || parseFloat(feeAmount) <= 0) {
            setFeeResult({ success: false, error: 'Please enter a valid amount' });
            return;
        }

        setCalculatingFees(true);
        setFeeResult(null);

        const result = await dispatch(calculateFees(tenantId, parseFloat(feeAmount)));

        setFeeResult(result);
        setCalculatingFees(false);
    };

    if (userRole !== 'admin') {
        return <div>Access denied. Admin privileges required.</div>;
    }

    const renderField = (label, name, value, type = 'text') => {
        const displayValue = value !== undefined && value !== null && value !== '' ? value : (defaultValues[name] || '');
        return (
            <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                    {label}
                </label>
                {isEditing ? (
                    <input
                        type={type}
                        step={type === 'number' ? '0.1' : undefined}
                        name={name}
                        value={value !== undefined && value !== null ? value : ''}
                        onChange={handleInputChange}
                        placeholder={defaultValues[name]?.toString() || ''}
                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                ) : (
                    <div className="w-full px-3 py-2 bg-gray-100 rounded-lg">
                        {displayValue}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen flex justify-center bg-gray-50">
            <div className="w-full max-w-4xl flex flex-col items-center space-y-3 px-4 md:px-16 py-8">
                <LogoSection />
                <div className="w-full max-w-4xl bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold">Tenant Details</h1>
                        {!isEditing && (
                            <button
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                            >
                                Edit
                            </button>
                        )}
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderField("Tenant Name", "tenantName", formData.tenantName)}
                        {renderField("Tenant ID", "id", formData.id)}
                        {renderField("Logo URL", "logo", formData.logo)}
                        {renderField("Organization ID", "org_id", formData.org_id)}
                        {renderField("Client Account ID", "clientAccountId", formData.clientAccountId)}
                        {renderField("Cash URL", "cash_url", formData.cash_url)}
                            {renderField("Default Target", "defaultTarget", formData.defaultTarget)}
                            {renderField("Default Target ID", "defaultTargetId", formData.defaultTargetId)}
                            {renderField("Fixed Rate", "fixedRate", formData.fixedRate, "number")}
                            {renderField("Variable Percentage", "variablePercentage", formData.variablePercentage, "number")}
                            {renderField("Terminal ID", "terminalId", formData.terminalId)}
                            {renderField("Amount 1", "amt1", formData.amt1, "number")}
                            {renderField("Amount 2", "amt2", formData.amt2, "number")}
                            {renderField("Amount 3", "amt3", formData.amt3, "number")}
                            {renderField("Amount 4", "amt4", formData.amt4, "number")}
                        </div>

                        {/* Band Configuration Table */}
                        <div className="mt-8 border-t border-gray-200 pt-6">
                            <h2 className="text-xl font-bold mb-4">Band Configuration</h2>
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    Currency (CCY)
                                </label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        name="CCY"
                                        value={formData.CCY || defaultValues.CCY}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                ) : (
                                    <div className="w-full px-3 py-2 bg-gray-100 rounded-lg">
                                        {formData.CCY || defaultValues.CCY}
                                    </div>
                                )}
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Band</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Min</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Max</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Fixed ($)</th>
                                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Variable (%)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Band 1 */}
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 border-b">Band 1</td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band1MinAmt"
                                                        value={formData.Band1MinAmt !== undefined ? formData.Band1MinAmt : defaultValues.Band1MinAmt}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band1MinAmt !== undefined ? formData.Band1MinAmt : defaultValues.Band1MinAmt}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band1MaxAmt"
                                                        value={formData.Band1MaxAmt !== undefined ? formData.Band1MaxAmt : defaultValues.Band1MaxAmt}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band1MaxAmt !== undefined ? formData.Band1MaxAmt : defaultValues.Band1MaxAmt}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band1Fixed"
                                                        value={formData.Band1Fixed !== undefined ? formData.Band1Fixed : defaultValues.Band1Fixed}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">${formData.Band1Fixed !== undefined ? formData.Band1Fixed : defaultValues.Band1Fixed}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band1Variable"
                                                        value={formData.Band1Variable !== undefined ? formData.Band1Variable : defaultValues.Band1Variable}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band1Variable !== undefined ? formData.Band1Variable : defaultValues.Band1Variable}%</span>
                                                )}
                                            </td>
                                        </tr>
                                        {/* Band 2 */}
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 border-b">Band 2</td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band2MinAmt"
                                                        value={formData.Band2MinAmt !== undefined ? formData.Band2MinAmt : defaultValues.Band2MinAmt}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band2MinAmt !== undefined ? formData.Band2MinAmt : defaultValues.Band2MinAmt}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band2MaxAmt"
                                                        value={formData.Band2MaxAmt !== undefined ? formData.Band2MaxAmt : defaultValues.Band2MaxAmt}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band2MaxAmt !== undefined ? formData.Band2MaxAmt : defaultValues.Band2MaxAmt}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band2Fixed"
                                                        value={formData.Band2Fixed !== undefined ? formData.Band2Fixed : defaultValues.Band2Fixed}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">${formData.Band2Fixed !== undefined ? formData.Band2Fixed : defaultValues.Band2Fixed}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band2Variable"
                                                        value={formData.Band2Variable !== undefined ? formData.Band2Variable : defaultValues.Band2Variable}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band2Variable !== undefined ? formData.Band2Variable : defaultValues.Band2Variable}%</span>
                                                )}
                                            </td>
                                        </tr>
                                        {/* Band 3 */}
                                        <tr className="hover:bg-gray-50">
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900 border-b">Band 3</td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band3MinAmt"
                                                        value={formData.Band3MinAmt !== undefined ? formData.Band3MinAmt : defaultValues.Band3MinAmt}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band3MinAmt !== undefined ? formData.Band3MinAmt : defaultValues.Band3MinAmt}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band3MaxAmt"
                                                        value={formData.Band3MaxAmt !== undefined ? formData.Band3MaxAmt : defaultValues.Band3MaxAmt}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band3MaxAmt !== undefined ? formData.Band3MaxAmt : defaultValues.Band3MaxAmt}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band3Fixed"
                                                        value={formData.Band3Fixed !== undefined ? formData.Band3Fixed : defaultValues.Band3Fixed}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">${formData.Band3Fixed !== undefined ? formData.Band3Fixed : defaultValues.Band3Fixed}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 border-b">
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        name="Band3Variable"
                                                        value={formData.Band3Variable !== undefined ? formData.Band3Variable : defaultValues.Band3Variable}
                                                        onChange={handleInputChange}
                                                        className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    />
                                                ) : (
                                                    <span className="text-gray-700">{formData.Band3Variable !== undefined ? formData.Band3Variable : defaultValues.Band3Variable}%</span>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="flex space-x-4 mt-6">
                            {isEditing && (
                                <>
                                    <button
                                        type="submit"
                                        className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
                                    >
                                        Save Changes
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={() => navigate('/manage-targets')}
                                className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
                            >
                                Back
                            </button>
                        </div>
                    </form>

                    {/* Fees Calculator Section */}
                    <div className="mt-8 border-t border-gray-200 pt-6">
                        <h2 className="text-xl font-bold mb-4">Fees Calculator</h2>
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="flex gap-4 items-end mb-4">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Amount
                                    </label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={feeAmount}
                                        onChange={(e) => setFeeAmount(e.target.value)}
                                        placeholder="Enter amount"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <button
                                    onClick={handleCalculateFees}
                                    disabled={calculatingFees || !feeAmount}
                                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                        calculatingFees || !feeAmount
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                >
                                    {calculatingFees ? 'Calculating...' : 'Calculate Fees'}
                                </button>
                            </div>

                            {feeResult && (
                                <div className={`p-4 rounded-lg border ${
                                    feeResult.success
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-red-50 border-red-200'
                                }`}>
                                    {feeResult.success ? (
                                        <div>
                                            <h3 className="text-lg font-semibold text-green-800 mb-2">Fee Calculation Result</h3>
                                            <div className="space-y-1 text-green-700">
                                                <p><strong>Amount:</strong> {feeResult.amount}</p>
                                                <p><strong>Currency:</strong> {feeResult.currency}</p>
                                                <p><strong>Band:</strong> {feeResult.band}</p>
                                                <p className="text-lg font-bold"><strong>Fees:</strong> {feeResult.currency} {feeResult.fees.toFixed(2)}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <h3 className="text-lg font-semibold text-red-800 mb-2">Error</h3>
                                            <p className="text-red-700">{feeResult.error}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stripe Details Section */}
                    <div className="mt-8 border-t border-gray-200 pt-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Stripe Details</h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={handleFetchLocations}
                                    disabled={loadingLocations || !formData.clientAccountId}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        loadingLocations || !formData.clientAccountId
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-blue-500 text-white hover:bg-blue-600'
                                    }`}
                                >
                                    {loadingLocations ? 'Loading...' : 'Fetch Locations'}
                                </button>
                                <button
                                    onClick={() => setShowCreateLocationForm(true)}
                                    disabled={!formData.clientAccountId}
                                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                        !formData.clientAccountId
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-green-500 text-white hover:bg-green-600'
                                    }`}
                                >
                                    Create Location
                                </button>
                            </div>
                        </div>

                        {/* Create Location Form */}
                        {showCreateLocationForm && (
                            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <h3 className="text-lg font-semibold mb-4">Create New Location</h3>
                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Display Name (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={newLocation.display_name}
                                            onChange={(e) => setNewLocation(prev => ({ ...prev, display_name: e.target.value }))}
                                            placeholder="e.g., Donate"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Address Line 1
                                            </label>
                                            <input
                                                type="text"
                                                value={newLocation.address.line1}
                                                onChange={(e) => handleAddressChange('line1', e.target.value)}
                                                placeholder="700-305 Milner Ave"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                value={newLocation.address.city}
                                                onChange={(e) => handleAddressChange('city', e.target.value)}
                                                placeholder="Toronto"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                State/Province
                                            </label>
                                            <input
                                                type="text"
                                                value={newLocation.address.state}
                                                onChange={(e) => handleAddressChange('state', e.target.value)}
                                                placeholder="ON"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Postal Code
                                            </label>
                                            <input
                                                type="text"
                                                value={newLocation.address.postal_code}
                                                onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                                                placeholder="M1B 3V4"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Country
                                            </label>
                                            <input
                                                type="text"
                                                value={newLocation.address.country}
                                                onChange={(e) => handleAddressChange('country', e.target.value)}
                                                placeholder="CA"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2 pt-2">
                                        <button
                                            onClick={handleCreateLocation}
                                            disabled={creatingLocation}
                                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                                creatingLocation
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-green-500 text-white hover:bg-green-600'
                                            }`}
                                        >
                                            {creatingLocation ? 'Creating...' : 'Create Location'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setShowCreateLocationForm(false);
                                                setNewLocation({
                                                    display_name: '',
                                                    address: {
                                                        line1: '700-305 Milner Ave',
                                                        city: 'Toronto',
                                                        state: 'ON',
                                                        country: 'CA',
                                                        postal_code: 'M1B 3V4'
                                                    }
                                                });
                                                setCreateLocationMessage(null);
                                            }}
                                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {createLocationMessage && (
                            <div className={`mb-4 p-3 rounded-lg ${
                                createLocationMessage.type === 'success'
                                    ? 'bg-green-50 border border-green-200 text-green-700'
                                    : 'bg-red-50 border border-red-200 text-red-700'
                            }`}>
                                {createLocationMessage.text}
                            </div>
                        )}
                        
                        {locationsError && (
                            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                                {locationsError}
                            </div>
                        )}
                        
                        {locations.length > 0 && (
                            <div className="space-y-2">
                                <h3 className="text-lg font-semibold mb-3">Locations</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">ID</th>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Display Name</th>
                                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b">Address</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {locations.map((location) => (
                                                <tr key={location.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm text-gray-900 border-b">{location.id}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 border-b">{location.display_name || 'N/A'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 border-b">
                                                        {location.address ? (
                                                            <div>
                                                                {location.address.line1 && <div>{location.address.line1}</div>}
                                                                {location.address.city && location.address.state && (
                                                                    <div>{location.address.city}, {location.address.state} {location.address.postal_code}</div>
                                                                )}
                                                                {location.address.country && <div>{location.address.country}</div>}
                                                            </div>
                                                        ) : 'N/A'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                        
                        {locations.length === 0 && !loadingLocations && !locationsError && (
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <p className="text-yellow-800 font-medium">Zero locations setup, please add a location</p>
                            </div>
                        )}
                        
                        {locations.length === 0 && !loadingLocations && locationsError && (
                            <p className="text-gray-500 text-sm">Click "Fetch Locations" to load Stripe locations for this tenant.</p>
                        )}
                    </div>

                    <div className="mt-8 border-t border-gray-200 pt-6">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold">Fundraising Targets</h2>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => dispatch(refreshTargets(formData.id))}
                                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                >
                                    Refresh Targets
                                </button>
                                <Link
                                    to={`/tenant/${tenantId}/add-target`}
                                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                                >
                                    + Add New Target
                                </Link>
                            </div>
                        </div>
                        <div className="space-y-2">
                            {targets.map(target => (
                                <div key={target.id} className="flex justify-between items-center p-4 bg-white rounded-lg shadow">
                                    <span className="font-medium">{target.name}</span>
                                    <Link
                                        to={`/tenant/${tenantId}/target/${target.id}`}
                                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                                    >
                                        Edit Target
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TenantDetails; 
